"use client";

import Link from "@/lib/router/link";
import { usePathname } from "@/lib/router/navigation";
import { Plus, Search } from "lucide-react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { UserMenu } from "./user-menu";
import { ThemeToggle } from "./theme-toggle";
import { NotificationsBell } from "./notifications-bell";
import { Can } from "@/lib/rbac/can";

/**
 * `notifications` is off in the platform console: a platform operator acts at
 * platform scope with no organisation, so there are no in-app notifications
 * to show and `GET /v1/notifications` would be refused every poll.
 */
export function Topbar({ notifications = true }: { notifications?: boolean } = {}) {
  const pathname = usePathname();
  const ws = pathname.match(/^\/projects\/([^/]+)/)?.[1];

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-background/85 px-3 backdrop-blur-md md:px-4">
      <SidebarTrigger className="text-muted-foreground" />

      <button
        onClick={() => window.dispatchEvent(new Event("sentinel:command"))}
        className="group ml-1 hidden h-9 max-w-md flex-1 items-center gap-2 rounded-lg border bg-muted/40 px-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-background md:flex"
      >
        <Search className="size-4" />
        <span>Search or jump to…</span>
        <kbd className="ml-auto hidden items-center gap-0.5 rounded border bg-background px-1.5 font-mono text-[10px] lg:inline-flex">⌘K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-1">
        {ws && (
          <Can perm="scans:run">
            <Button asChild size="sm" className="gap-1.5">
              <Link href={`/projects/${ws}/scans/new`}><Plus className="size-4" /> New Scan</Link>
            </Button>
          </Can>
        )}
        {notifications && <NotificationsBell />}
        <ThemeToggle />
        <Separator orientation="vertical" className="mx-1 h-6" />
        <UserMenu />
      </div>
    </header>
  );
}
